import React, { useContext } from 'react';
import Select from 'react-select';
import { BoxContext } from './BoxContext';
import { Package, Check, AlertTriangle, Minus } from 'lucide-react';

const CustomDropdown = ({ shipmentItems, onChange, value }) => {
  const { selectedPlan, boxes, selectedPackingGroup } = useContext(BoxContext);

  // Filter shipment items based on the selected plan or packing group
  const filteredShipmentItems = shipmentItems
    .filter(item => {
      // Use selectedPackingGroup if it exists, otherwise use selectedPlan.items
      if (selectedPackingGroup) {
        // Create a map of plan item IDs to MSKUs
        const planItemsMap = new Map(
          selectedPlan?.items?.map(item => [item.id, item.msku]) || []
        );
        
        // Get MSKUs from the packing group's plan item IDs
        const packingGroupMskus = new Set(
          selectedPackingGroup.items
            .map(item => planItemsMap.get(item.planItemId))
            .filter(Boolean)
        );
        
        return packingGroupMskus.has(item.item.merchant_sku);
      } else if (selectedPlan && selectedPlan.items) {
        return selectedPlan.items.some(planItem => planItem.msku === item.item.merchant_sku);
      }
      // If no plan is selected or plan doesn't have items, show all shipment items
      return true;
    })
    .map(item => {
      // Find the corresponding plan item
      const planItem = selectedPlan?.items?.find(
        planItem => planItem.msku === item.item.merchant_sku
      );
      return { ...item, quantity: planItem ? planItem.quantity : item.quantity };
    });

  // Calculate boxed quantity for each SKU
  const calculateBoxedQuantity = (merchantSku) => {
    let boxedQuantity = 0;
    boxes.forEach(box => {
      box.items.forEach(boxItem => {
        if (boxItem.SKU === merchantSku) {
          boxedQuantity += boxItem.QTY;
        }
      });
    });
    return boxedQuantity;
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 0,
      border: '1px solid #d1d5db',
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      '&:hover': {
        borderColor: '#9ca3af'
      },
      fontSize: '14px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      fontSize: '14px',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '6px',
      color: '#6b7280',
    }),
    option: (provided, state) => ({
      ...provided,
      borderRadius: 0,
      padding: '12px 16px',
      backgroundColor: state.isSelected 
        ? '#f3f4f6' 
        : state.isFocused 
        ? '#f9fafb' 
        : 'white',
      color: '#374151',
      fontSize: '14px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#f3f4f6',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '14px',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: 0,
      border: '1px solid #d1d5db',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginTop: 1,
      overflow: 'hidden',
      width: '120%',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: '300px',
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      fontSize: '14px',
      color: '#6b7280',
      padding: '16px',
    }),
  };

  const getStatusInfo = (boxedQuantity, totalQuantity) => {
    if (boxedQuantity === totalQuantity && totalQuantity > 0) {
      return {
        icon: Check,
        bgColor: '#f0f9ff',
        textColor: '#0369a1',
        borderColor: '#0369a1',
        status: 'Complete'
      };
    } else if (boxedQuantity > totalQuantity) {
      return {
        icon: AlertTriangle,
        bgColor: '#fef2f2',
        textColor: '#dc2626',
        borderColor: '#dc2626',
        status: 'Over'
      };
    } else if (boxedQuantity > 0 && boxedQuantity < totalQuantity) {
      return {
        icon: Minus,
        bgColor: '#fffbeb',
        textColor: '#d97706',
        borderColor: '#d97706',
        status: 'Partial'
      };
    } else {
      return {
        icon: Package,
        bgColor: '#f9fafb',
        textColor: '#6b7280',
        borderColor: '#d1d5db',
        status: 'Pending'
      };
    }
  };

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-gray-900 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const formatOptionLabel = ({ item }, { inputValue }) => {
    const boxedQuantity = calculateBoxedQuantity(item.merchant_sku);
    const totalQuantity = filteredShipmentItems.find(filteredItem => 
      filteredItem.item.merchant_sku === item.merchant_sku
    )?.quantity || 0;
    
    const statusInfo = getStatusInfo(boxedQuantity, totalQuantity);
    const StatusIcon = statusInfo.icon;

    return (
      <div className="flex items-center gap-4 w-full">
        {/* Product Image with Status Below */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2">
          {item.images && item.images.length > 0 ? (
            <div className="w-20 h-20 bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
              <img 
                src={item.images[0].large_url} 
                alt={item.title} 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <Package className="w-8 h-8 text-gray-400" style={{display: 'none'}} />
            </div>
          ) : (
            <div className="w-20 h-20 bg-gray-100 border border-gray-200 flex items-center justify-center">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
          )}
          
          {/* Status Indicator Under Thumbnail */}
          <div 
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium border"
            style={{
              backgroundColor: statusInfo.bgColor,
              color: statusInfo.textColor,
              borderColor: statusInfo.borderColor,
            }}
          >
            <StatusIcon className="w-3 h-3" />
            <span>{boxedQuantity}/{totalQuantity}</span>
          </div>
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 leading-tight">
            {highlightMatch(item.title, inputValue)}
          </div>
          <div className="text-sm text-gray-600 font-mono font-bold mt-1">
            {highlightMatch(item.asin, inputValue)} • {highlightMatch(item.fnsku, inputValue)} • {highlightMatch(item.merchant_sku, inputValue)}
            {item.upc && <span> • {highlightMatch(item.upc, inputValue)}</span>}
          </div>
        </div>
      </div>
    );
  };

  const formatSingleValue = ({ item }) => {
    const boxedQuantity = calculateBoxedQuantity(item.merchant_sku);
    const totalQuantity = filteredShipmentItems.find(filteredItem => 
      filteredItem.item.merchant_sku === item.merchant_sku
    )?.quantity || 0;
    
    const statusInfo = getStatusInfo(boxedQuantity, totalQuantity);
    const StatusIcon = statusInfo.icon;

    return (
      <div className="flex items-center gap-3 w-full">
        <div 
          className="flex items-center gap-1.5 px-2 py-1 text-sm font-medium border"
          style={{
            backgroundColor: statusInfo.bgColor,
            color: statusInfo.textColor,
            borderColor: statusInfo.borderColor,
          }}
        >
          <StatusIcon className="w-4 h-4" />
          <span>{boxedQuantity}/{totalQuantity}</span>
        </div>
        <span className="text-base font-semibold text-gray-900 truncate">{item.title}</span>
      </div>
    );
  };

  const options = filteredShipmentItems
    .map((item) => {
      const boxedQuantity = calculateBoxedQuantity(item.item.merchant_sku);
      const totalQuantity = item.quantity || 0;
      const isFullyBoxed = boxedQuantity >= totalQuantity && totalQuantity > 0;
      
      return {
        value: item.item.asin,
        label: item.item.title,
        item: item.item,
        boxedQuantity,
        totalQuantity,
        isFullyBoxed
      };
    })
    .sort((a, b) => {
      // Prioritize items that are not fully boxed
      if (a.isFullyBoxed && !b.isFullyBoxed) return 1;
      if (!a.isFullyBoxed && b.isFullyBoxed) return -1;
      
      // Among non-fully boxed items, prioritize those with less percentage boxed
      if (!a.isFullyBoxed && !b.isFullyBoxed) {
        const aPercentage = a.totalQuantity > 0 ? a.boxedQuantity / a.totalQuantity : 0;
        const bPercentage = b.totalQuantity > 0 ? b.boxedQuantity / b.totalQuantity : 0;
        return aPercentage - bPercentage;
      }
      
      // Otherwise, sort alphabetically by title
      return a.label.localeCompare(b.label);
    });

  const filterOption = ({ label, value, data }, inputValue) => {
    const searchTerm = inputValue.toLowerCase();
    return (
      label.toLowerCase().includes(searchTerm) ||
      data.item.fnsku.toLowerCase().includes(searchTerm) ||
      data.item.merchant_sku.toLowerCase().includes(searchTerm) ||
      data.item.asin.toLowerCase().includes(searchTerm) ||
      (data.item.upc && data.item.upc.toLowerCase().includes(searchTerm))
    );
  };

      return (
      <div className="w-full">
        <Select
          options={options}
          styles={customStyles}
          formatOptionLabel={formatOptionLabel}
          formatSingleValue={formatSingleValue}
          onChange={(selectedOption) => onChange(selectedOption.item)}
          value={options.find(option => option.value === value) || null}
          placeholder="Search by title, ASIN, FNSKU, SKU, or UPC..."
          filterOption={filterOption}
          isSearchable
          className="text-sm"
        />
      </div>
    );
};

export default CustomDropdown;